// motion.jsx - Movement. Scroll reveals, staggered groups, counting numbers, an
// infinite strip, and a pointer-follow tilt.
//
// Three rules everything here obeys:
//   1. Nothing is hidden without JavaScript. The initial hidden state lives under
//      the `.js` class that index.html sets before first paint, so a crawler and
//      a scripting-off browser both see a complete page.
//   2. Everything respects prefers-reduced-motion, in CSS rather than in JS, so
//      it holds even for elements this file never touches.
//   3. One shared IntersectionObserver for the whole page, not one per element.
//      A long page has forty reveal targets and forty observers is forty times
//      the work for the same result.
//
// LEGEND
//   1. observer()   the shared IntersectionObserver
//   2. Enter        an entrance that needs no JavaScript, for above the fold
//   3. Reveal       one element, revealed when it scrolls into view
//   4. Stagger      a group whose children reveal in sequence
//   5. CountUp      a number that counts to its value when seen
//   6. Marquee      an infinite horizontal strip
//   7. Tilt         a card that leans towards the pointer
//   8. ScrollProgress  the reading bar on an article
//   9. useReducedMotion / useInView  the two hooks the rest of the app uses

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

////////// 1. SHARED OBSERVER //////////
// Targets register themselves and are unobserved the moment they fire, because a
// reveal only ever happens once.

let shared = null;
const callbacks = new WeakMap();

function observer() {
  if (typeof window === "undefined") return null;
  if (shared) return shared;

  shared = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        callbacks.get(entry.target)?.();
        shared.unobserve(entry.target);
        callbacks.delete(entry.target);
      }
    },
    // A little before the element arrives, so it is already settled by the time
    // it is properly on screen.
    { rootMargin: "0px 0px -12% 0px", threshold: 0.08 }
  );
  return shared;
}

export function useInView() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    const io = observer();
    if (!node || !io) { setSeen(true); return undefined; }

    // Anything already on screen at mount reveals immediately rather than
    // waiting for a scroll that may never come.
    const box = node.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) { setSeen(true); return undefined; }

    callbacks.set(node, () => setSeen(true));
    io.observe(node);
    return () => { io.unobserve(node); callbacks.delete(node); };
  }, []);

  return [ref, seen];
}

////////// 2. ENTER //////////
// For anything visible when the page loads. It animates from a pure CSS keyframe,
// so it plays the moment the stylesheet is parsed rather than waiting for React
// to hydrate. Scroll reveals must never be used above the fold: they would leave
// the hero blank for as long as the JavaScript takes to arrive.

export function Enter({ as: Tag = "div", delay = 0, from = "up", className, children, ...rest }) {
  return (
    <Tag
      data-enter={from}
      style={delay ? { "--enter-delay": `${delay}ms` } : undefined}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
}

////////// 3. REVEAL //////////
// `as` keeps the semantics right: a reveal around a list item must still be an
// <li>, not a wrapping <div> that breaks the list.

export function Reveal({
  as: Tag = "div",
  from = "up",
  delay = 0,
  className,
  children,
  ...rest
}) {
  const [ref, seen] = useInView();

  return (
    <Tag
      ref={ref}
      data-reveal={from}
      data-seen={seen ? "true" : undefined}
      style={delay ? { "--reveal-delay": `${delay}ms` } : undefined}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
}

////////// 4. STAGGER //////////
// The parent watches, the children inherit an increasing delay. One observer for
// a whole grid instead of one per card.

export function Stagger({
  as: Tag = "div",
  from = "up",
  step = 70,
  start = 0,
  className,
  children,
  ...rest
}) {
  const [ref, seen] = useInView();
  const items = Array.isArray(children) ? children : [children];

  return (
    <Tag ref={ref} className={className} {...rest}>
      {items.filter(Boolean).map((child, index) => (
        <div
          key={child?.key ?? index}
          data-reveal={from}
          data-seen={seen ? "true" : undefined}
          style={{ "--reveal-delay": `${start + index * step}ms`, display: "contents" }}
        >
          {child}
        </div>
      ))}
    </Tag>
  );
}

// When `display: contents` would break a grid or a table, this variant keeps the
// wrapper as a real box instead.
//
// That wrapper is the grid item, not the child inside it, so anything about how
// a child sits in the grid has to be set here: a `col-span` on the child would
// be a span on a box that is not participating in the grid at all. Hence
// `itemClassName` also taking a function of the index, for the one common case
// of a first row that is wider than the rest.
export function StaggerBoxed({ as: Tag = "div", from = "up", step = 70, start = 0, itemClassName, className, children, ...rest }) {
  const [ref, seen] = useInView();
  const items = Array.isArray(children) ? children : [children];

  return (
    <Tag ref={ref} className={className} {...rest}>
      {items.filter(Boolean).map((child, index) => (
        <div
          key={child?.key ?? index}
          data-reveal={from}
          data-seen={seen ? "true" : undefined}
          style={{ "--reveal-delay": `${start + index * step}ms` }}
          className={typeof itemClassName === "function" ? itemClassName(index) : itemClassName}
        >
          {child}
        </div>
      ))}
    </Tag>
  );
}

////////// 5. COUNT UP //////////
// Eases out, so it decelerates into the final number rather than stopping dead.

export function CountUp({ to, duration = 1100, prefix = "", suffix = "", decimals = 0, format, className }) {
  const [ref, seen] = useInView();
  const reduced = useReducedMotion();
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!seen) return undefined;
    if (reduced || !Number.isFinite(to)) { setValue(to); return undefined; }

    let frame;
    const started = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - started) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setValue(to * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [seen, to, duration, reduced]);

  const shown = format ? format(value) : `${prefix}${value.toFixed(decimals)}${suffix}`;
  return <span ref={ref} className={clsx("num tabular-nums", className)}>{shown}</span>;
}

////////// 6. MARQUEE //////////
// The track is rendered twice and translated by exactly half its width, which is
// what makes the loop seamless. Duration scales with the item count so adding
// items does not speed it up.

export function Marquee({ items, speed = 46, className, itemClassName, reverse = false }) {
  const duration = Math.max(18, (items.length * speed) / 4);

  return (
    <div className={clsx("marquee", className)} aria-hidden="true">
      <div
        className="marquee-track"
        style={{ animationDuration: `${duration}s`, animationDirection: reverse ? "reverse" : "normal" }}
      >
        {[0, 1].map((copy) => (
          <div key={copy} className="marquee-run">
            {items.map((item, index) => (
              <span key={`${copy}-${index}`} className={itemClassName}>{item}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

////////// 7. TILT //////////
// Six degrees at most. Anything more reads as a gimmick, and it is switched off
// entirely on touch, where there is no pointer to follow.

export function Tilt({ max = 5, className, children, ...rest }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  const onMove = useCallback((event) => {
    const node = ref.current;
    if (!node || reduced) return;
    const box = node.getBoundingClientRect();
    const x = (event.clientX - box.left) / box.width - 0.5;
    const y = (event.clientY - box.top) / box.height - 0.5;
    node.style.setProperty("--tilt-x", `${(-y * max).toFixed(2)}deg`);
    node.style.setProperty("--tilt-y", `${(x * max).toFixed(2)}deg`);
  }, [max, reduced]);

  const reset = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    node.style.setProperty("--tilt-x", "0deg");
    node.style.setProperty("--tilt-y", "0deg");
  }, []);

  return (
    <div
      ref={ref}
      className={clsx("tilt", className)}
      onPointerMove={onMove}
      onPointerLeave={reset}
      {...rest}
    >
      {children}
    </div>
  );
}

////////// 8. SCROLL PROGRESS //////////

export function ScrollProgress({ target }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const node = target?.current;
      const total = node
        ? node.offsetTop + node.offsetHeight - window.innerHeight
        : document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(1, Math.max(0, window.scrollY / total)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [target]);

  return (
    <div className="fixed top-0 left-0 right-0 h-[2px] z-50 pointer-events-none" aria-hidden="true">
      <div
        className="h-full bg-bronze origin-left transition-transform duration-75"
        style={{ transform: `scaleX(${progress})` }}
      />
    </div>
  );
}

////////// 9. REDUCED MOTION //////////

export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = (event) => setReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

// A value that eases towards a target on every scroll frame. Used for the very
// light parallax on the hero, where a hard binding to scrollY looks mechanical.
export function useParallax(strength = 0.06) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    let frame = 0;
    let current = 0;

    const loop = () => {
      const box = node.getBoundingClientRect();
      const centred = box.top + box.height / 2 - window.innerHeight / 2;
      const target = -centred * strength;
      current += (target - current) * 0.08;
      if (Math.abs(target - current) > 0.1) {
        node.style.transform = `translate3d(0, ${current.toFixed(2)}px, 0)`;
      }
      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [strength, reduced]);

  return ref;
}
